import { useMemo, useRef, useState } from 'react';

import type { ValidationState } from '@ui-component-library/types/status';

import { SuffixIcon } from '@ui-component-library/components/_deprecated/Input/SuffixIcon/SuffixIcon';
import { Icon } from '@ui-component-library/components/Icon';
import { Tooltip } from '@ui-component-library/components/Tooltip';

import { useDebounce } from '@ui-component-library/hooks/useDebounce';

import type { Error, InputProps, Option } from './SearchDropdown.types';

import { S } from './SearchDropdown.styles';

export const SearchDropdown = ({
  variant = 'default',
  defaultValue = '',
  disabled = false,
  required = false,

  label,
  description,
  placeholder,

  idleText,
  errorText,
  successText,

  validation,
  name,
  onChange,
  search,
  tabIndex = 0
}: InputProps) => {
  console.warn(
    `The "SearchDropdown" component is deprecated. Please use the "SearchInput" component instead.`,
    `See stories for examples.`
  );
  const [value, setValue] = useState(defaultValue);
  const [options, setOptions] = useState<Option[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [debounceTimeoutID, setDebounceTimeoutID] = useState<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const debouncedValue = useDebounce(value, 500);

  const helperTexts = {
    idle: idleText,
    error: errorText,
    success: successText
  };

  const isFluid = variant === 'fluid';
  const [isLabelFloating, setIsLabelFloating] = useState(!!value);

  const isValueValid = useMemo(() => {
    if (!debouncedValue) {
      return required ? false : true;
    }
    return validation(debouncedValue);
  }, [debouncedValue, validation, required]);

  const doSearch = async (val: string) => {
    setIsSearching(true);
    const searchOptions = await search(val);

    if ((searchOptions as Error)?.error) {
      setValidationState('error');
      setIsSearching(false);
      return;
    }
    setOptions(searchOptions as Option[]);
    setTimeout(() => {
      setIsSearching(false);
    }, 50);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setValue(val);
    if (debouncedValue === val) {
      doSearch(val);
    } else {
      if (debounceTimeoutID) {
        clearTimeout(debounceTimeoutID);
      }
      const timeout = setTimeout(() => {
        doSearch(val);
      }, 550);
      setDebounceTimeoutID(timeout);
    }
  };

  const handleOptionClick = (option: Option) => {
    setValue(option.label);

    setValidationState(isValueValid ? 'success' : 'error');
    onChange(option, isValueValid, name);

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (isFluid) {
      setIsLabelFloating(true);
    }
  };

  const handleBlur = () => {
    //wait for new focus to not be input then run check if option was focused instead
    setInterval(() => {
      if (document.activeElement === inputRef.current) {
        return;
      }

      if (document.activeElement?.parentElement !== optionsContainerRef.current) {
        setIsFocused(false);
        setOptions([]);
        if (isFluid && !value) {
          setIsLabelFloating(false);
        }
      } else if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 10);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setValue('');
    setValidationState('idle');

    // Immediately notify of clear action - don't wait for debounce
    onChange('', false, name);

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  const shouldShowHelperText = () => {
    if (disabled || !helperTexts[validationState]) {
      return false;
    }

    return validationState === 'error' || isFocused;
  };

  return (
    <S.Wrapper variant={variant} disabled={disabled || false}>
      {!isFluid && (
        <S.TopSection>
          <S.Label htmlFor={name}>
            {label}
            {required && <>*</>}
          </S.Label>
          {!disabled && <S.Description>{description}</S.Description>}
        </S.TopSection>
      )}
      <S.InputContainer $state={disabled ? 'disabled' : validationState}>
        {isFluid && (
          <S.FluidLabel $isFloating={isLabelFloating} htmlFor={name}>
            <Tooltip content={description} aria-label={`More information about ${label}`}>
              {label}
              {required && <>*</>}

              <S.FluidLabelIcon>
                <Icon name="InfoFilled" size="small" color="charcoal-500" aria-hidden="true" />
              </S.FluidLabelIcon>
            </Tooltip>
          </S.FluidLabel>
        )}
        <S.Input
          ref={inputRef}
          type="text"
          value={value || ''}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={isFluid ? '' : placeholder}
          $isFluid={isFluid}
          aria-describedby={description}
          tabIndex={tabIndex}
        />
        <S.SuffixWrapper>
          <SuffixIcon
            isTyping={isSearching}
            isFocused={isFocused}
            validationState={validationState}
            handleClear={handleClear}
          />
        </S.SuffixWrapper>
      </S.InputContainer>
      {isFocused && options.length > 0 && (
        <S.OptionsContainer ref={optionsContainerRef}>
          {options.map((opt) => (
            <S.Option
              key={opt.value}
              tabIndex={0}
              onClick={() => {
                handleOptionClick(opt);
              }}
            >
              {opt.label}
            </S.Option>
          ))}
        </S.OptionsContainer>
      )}
      <S.BottomSection>
        {shouldShowHelperText() && (
          <S.HelperText $validationState={validationState}>
            {helperTexts[validationState]}
          </S.HelperText>
        )}
      </S.BottomSection>
    </S.Wrapper>
  );
};
